import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { Calendar, ArrowRight, BookOpen, Search } from "lucide-react";
import SEO from "@/components/SEO";
import { POSTS, getCategories } from "@/data/blog";
import { SITE_URL } from "@/config/site";

export default function Blog() {
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState("Tous");
  const categories = ["Tous", ...getCategories()];

  const filtered = useMemo(() => {
    return POSTS.filter(p => {
      const matchCat = category === "Tous" || p.category === category;
      const q = query.trim().toLowerCase();
      const matchQ = !q || p.title.toLowerCase().includes(q) || p.excerpt.toLowerCase().includes(q);
      return matchCat && matchQ;
    });
  }, [query, category]);

  const featured = filtered[0];
  const rest = filtered.slice(1);

  return (
    <>
      <SEO
        title="Blog Assurance Décennale & Construction | Perspective Assurance Lyon"
        description="Tous nos articles sur l'assurance décennale, dommage ouvrage, RC Pro, par métier du bâtiment. Conseils, exemples de sinistres, bonnes pratiques."
        canonical={`${SITE_URL}/blog`}
      />

      {/* Hero */}
      <section className="bg-cream py-16 lg:py-24 relative overflow-hidden">
        <div className="absolute inset-0 grain pointer-events-none" />
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-12 relative">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-soft rounded-full mb-6">
            <BookOpen className="w-3.5 h-3.5 text-terracotta" />
            <span className="font-outfit text-xs tracking-[0.2em] uppercase text-forest/80">Blog & expertise</span>
          </div>
          <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl text-forest leading-[1.05] tracking-tight max-w-3xl text-balance">
            L'assurance, <span className="italic text-terracotta">expliquée clairement.</span>
          </h1>
          <p className="mt-6 text-lg text-forest/70 max-w-2xl leading-relaxed">
            {POSTS.length} articles pour comprendre la décennale, la RC Pro, le dommage ouvrage et tous les risques par métier du bâtiment.
          </p>

          <div className="mt-10 flex flex-col md:flex-row gap-4 max-w-3xl">
            <div className="relative flex-1">
              <Search className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-forest/40" />
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Rechercher un article…"
                data-testid="blog-search"
                className="w-full bg-white border border-soft rounded-full pl-11 pr-5 py-3 focus:outline-none focus:ring-2 focus:ring-terracotta/40 focus:border-terracotta transition-all"
              />
            </div>
          </div>

          <div className="mt-6 flex flex-wrap gap-2">
            {categories.map(c => (
              <button
                key={c}
                onClick={() => setCategory(c)}
                data-testid={`blog-cat-${c.replace(/\s+/g, "-").toLowerCase()}`}
                className={`px-4 py-2 rounded-full font-outfit text-sm transition-all ${
                  category === c
                    ? "bg-forest text-white"
                    : "bg-white border border-soft text-forest hover:border-forest"
                }`}
              >
                {c}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Articles */}
      <section className="py-16 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-12">
          {filtered.length === 0 ? (
            <p className="text-center text-forest/60 py-20">Aucun article ne correspond à votre recherche.</p>
          ) : (
            <>
              {/* Featured */}
              {featured && (
                <Link
                  to={`/blog/${featured.slug}`}
                  data-testid={`featured-post-${featured.slug}`}
                  className="group grid lg:grid-cols-2 gap-8 lg:gap-14 mb-16 lg:mb-20"
                >
                  <div className="relative overflow-hidden rounded-3xl bg-cream aspect-[4/3]">
                    <img
                      src={featured.image}
                      alt={featured.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                      loading="lazy"
                    />
                  </div>
                  <div className="flex flex-col justify-center">
                    <div className="flex items-center gap-3 mb-4">
                      <span className="font-outfit text-xs tracking-[0.2em] uppercase text-terracotta">{featured.category}</span>
                      <span className="text-forest/30">·</span>
                      <span className="inline-flex items-center gap-1.5 text-xs text-forest/60">
                        <Calendar className="w-3 h-3" /> {featured.dateLabel}
                      </span>
                    </div>
                    <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl text-forest leading-tight tracking-tight group-hover:text-terracotta transition-colors mb-5">
                      {featured.title}
                    </h2>
                    <p className="text-forest/70 leading-relaxed text-lg mb-6">{featured.excerpt}</p>
                    <span className="inline-flex items-center gap-2 text-terracotta font-outfit font-medium">
                      Lire l'article <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                    </span>
                  </div>
                </Link>
              )}

              {/* Grid */}
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                {rest.map(p => (
                  <Link
                    key={p.slug}
                    to={`/blog/${p.slug}`}
                    data-testid={`post-card-${p.slug}`}
                    className="group flex flex-col"
                  >
                    <div className="relative overflow-hidden rounded-2xl bg-cream aspect-[4/3] mb-5">
                      <img
                        src={p.image}
                        alt={p.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        loading="lazy"
                      />
                    </div>
                    <div className="flex items-center gap-3 mb-3">
                      <span className="font-outfit text-[10px] tracking-[0.2em] uppercase text-terracotta">{p.category}</span>
                      <span className="text-forest/30">·</span>
                      <span className="inline-flex items-center gap-1 text-xs text-forest/60">
                        <Calendar className="w-3 h-3" /> {p.dateLabel}
                      </span>
                    </div>
                    <h3 className="font-display text-xl text-forest leading-snug group-hover:text-terracotta transition-colors mb-3">
                      {p.title}
                    </h3>
                    <p className="text-sm text-forest/65 leading-relaxed line-clamp-3">{p.excerpt}</p>
                    <span className="mt-4 inline-flex items-center gap-1.5 text-terracotta text-sm font-outfit font-medium">
                      Lire <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform" />
                    </span>
                  </Link>
                ))}
              </div>
            </>
          )}
        </div>
      </section>
    </>
  );
}
